import type { EnterpriseAdminAccountProperties, EnterpriseProperties } from '@coursera/event-pulse-types';
// createTrackerStore is a pure factory (it builds a React context); the
// tree-shaking plugin just can't infer that from the imported signature.
// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
import { createTrackerStore } from '@coursera/event-pulse/react';

/**
 * The `enterprise` + `enterpriseAdminAccount` entities every messaging event
 * carries, sourced once from `useEnterpriseAdminTrackingData()` and spread into
 * each `track(...)` call — matching the sibling enterprise-admin bundles.
 */
export type MessagingTrackingData = {
  enterprise: EnterpriseProperties;
  enterpriseAdminAccount: EnterpriseAdminAccountProperties;
};

/**
 * Holds the shared `enterprise` + `enterpriseAdminAccount` entities for the
 * messaging feature. Populated once by {@link MessagingTrackingDataProvider}
 * (mounted where the router/admin context is available) and spread into every
 * messaging `track(...)` call — mirroring the `createTrackerStore` pattern used
 * by other bundles (e.g. course-frame, ai-coach) to attach common entities to
 * all events.
 */
const [MessagingTrackingDataProvider, useMessagingTrackingDataStore] = createTrackerStore<MessagingTrackingData>({
  storeName: 'MessagingTrackingData',
});

/**
 * Empty default used when no provider is mounted above (e.g. a component
 * rendered in isolation in a unit test). Every field on the underlying schema
 * is optional, so this is a valid no-op payload.
 */
const EMPTY_MESSAGING_TRACKING_DATA: MessagingTrackingData = { enterprise: {}, enterpriseAdminAccount: {} };

/**
 * Read the shared messaging tracking entities to spread into `track(...)`
 * calls. Returns an empty (schema-valid) default when no
 * {@link MessagingTrackingDataProvider} is mounted, so leaf components stay
 * renderable on their own.
 */
export const useMessagingTrackingData = (): MessagingTrackingData =>
  useMessagingTrackingDataStore() ?? EMPTY_MESSAGING_TRACKING_DATA;

export { MessagingTrackingDataProvider };
